"use client";

import React, { useEffect, useRef, useState } from "react";
import { Button, Flex, Form, Grid, Tour, TourProps } from "antd/lib";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import XStroke from "../../../public/icons/XStroke";
import { useSidebarTutorial } from "@/context/SidebarTutorialContext";
import { SidebarTutorialSlugs } from "@/constants/tour";
import { getTourTarget, getPageFaqTourStep } from "@/utils/helpers";
import EventsList from "@/src/components/Events/EventsList";
import FilterPublicEvents from "@/src/components/Events/FilterPublicEvents";
import { useFilter } from "@/hooks/useFilter";
import { CheckboxOptionType } from "antd/lib/checkbox/Group";
import { FilterTypes } from "@/constants/common";
import { useSearch } from "@/hooks/useSearch";

const { useBreakpoint } = Grid;

dayjs.extend(customParseFormat);

const PublicEvents: React.FC = () => {
  const { t } = useTranslation();
  const [form] = Form.useForm();

  const ref1 = useRef(null);
  const ref2 = useRef(null);
  const ref3 = useRef(null);
  const ref4 = useRef(null);
  const ref5 = useRef(null);

  const { pageTourIsOpen, tourWasShown, setTourWasShown, setSidebarTutorialSlug, faqRef, pageFaqLinkRef } =
    useSidebarTutorial();
  const screens = useBreakpoint();

  const [tourIsOpen, setTourIsOpen] = useState<boolean>(pageTourIsOpen);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [equippingTypeValue, setEquippingTypeValue] = useState<string>();
  const [filtersOptionsAreLoading, setFiltersOptionsAreLoading] = useState(true);
  const [filtersOptionsError, setFiltersOptionsError] = useState(false);
  const [activityLanguages, setActivityLanguages] = useState<CheckboxOptionType[]>([]);
  const [activityMaterials, setActivityMaterials] = useState<CheckboxOptionType[]>([]);

  const {
    filtersCleared,
    applyFiltersHandler,
    resetFiltersHandler,
    searchParamsWillBeAdded,
    formValuesChangeHandler,
    formValuesChanged,
  } = useFilter(
    form,
    filtersOptionsAreLoading,
    FilterTypes.common,
    activityLanguages,
    activityMaterials,
    equippingTypeValue,
  );

  const { searchValue, searchInputChangeHandler, searchHandler, resetInputSearchHandler, showSearchInputReset } =
    useSearch(FilterTypes.common);

  const steps: TourProps["steps"] = [
    ...(pageFaqLinkRef ? [getPageFaqTourStep(t, pageFaqLinkRef)] : []),
    {
      title: t("common.tour.publicEvents.steps.search.title"),
      description: t("common.tour.publicEvents.steps.search.description"),
      target: () => ref1.current,
    },
    {
      title: t("common.tour.publicEvents.steps.filterByDate.title"),
      description: t("common.tour.publicEvents.steps.filterByDate.description"),
      target: () => ref2.current,
    },
    {
      title: t("common.tour.publicEvents.steps.filterByLocation.title"),
      description: t("common.tour.publicEvents.steps.filterByLocation.description"),
      target: () => ref3.current,
    },
    {
      title: t("common.tour.publicEvents.steps.filters.title"),
      description: t("common.tour.publicEvents.steps.filters.description"),
      target: () => ref4.current,
    },
    ...(faqRef
      ? [
          {
            title: t("common.tour.sidebar.steps.faq.title"),
            description: t("common.tour.sidebar.steps.faq.description"),
            target: getTourTarget(faqRef, ".ant-menu-item"),
          },
        ]
      : []),
  ];

  useEffect(() => {
    if (tourIsOpen !== pageTourIsOpen) setTourIsOpen(pageTourIsOpen);
  }, [pageTourIsOpen]);

  return (
    <Flex justify="space-between" gap={32} className="w-full">
      <Flex vertical gap={32} className="w-full">
        {!screens.xl && (
          <Flex justify="flex-end">
            <Button onClick={() => setIsDrawerOpen(true)}>{t("common.filters.title")}</Button>
          </Flex>
        )}
        <EventsList
          firstEventRef={ref5}
          searchParamsWillBeAdded={searchParamsWillBeAdded}
          filtersOptionsError={filtersOptionsError}
        />
      </Flex>

      <FilterPublicEvents
        searchRef={ref1}
        dateFilterRef={ref2}
        locationFilterRef={ref3}
        filtersRef={ref4}
        isDrawerOpen={isDrawerOpen}
        equippingTypeValue={equippingTypeValue}
        setEquippingTypeValue={setEquippingTypeValue}
        setIsDrawerOpen={setIsDrawerOpen}
        form={form}
        filtersCleared={filtersCleared}
        searchValue={searchValue}
        showSearchInputReset={showSearchInputReset}
        applyFiltersHandler={applyFiltersHandler}
        searchInputChangeHandler={searchInputChangeHandler}
        searchHandler={searchHandler}
        resetInputSearchHandler={resetInputSearchHandler}
        resetFiltersHandler={resetFiltersHandler}
        activityLanguages={activityLanguages}
        setActivityLanguages={setActivityLanguages}
        filtersOptionsAreLoading={filtersOptionsAreLoading}
        setFiltersOptionsAreLoading={setFiltersOptionsAreLoading}
        formValuesChangeHandler={formValuesChangeHandler}
        formValuesChanged={formValuesChanged}
        activityMaterials={activityMaterials}
        setActivityMaterials={setActivityMaterials}
        setFiltersOptionsError={setFiltersOptionsError}
      />

      {!filtersOptionsAreLoading && (
        <Tour
          placement="right"
          open={tourIsOpen}
          closeIcon={
            <XStroke
              color="#000"
              onClick={() => {
                setTourIsOpen(false);
                if (!tourWasShown.openTutorialIcon) setSidebarTutorialSlug(SidebarTutorialSlugs.openTutorialIcon);
                setTourWasShown({ ...tourWasShown, main: true });
              }}
            />
          }
          onFinish={() => {
            setTourIsOpen(false);
            if (!tourWasShown.openTutorialIcon) setSidebarTutorialSlug(SidebarTutorialSlugs.openTutorialIcon);
            setTourWasShown({ ...tourWasShown, main: true });
          }}
          steps={steps}
          indicatorsRender={() => <></>}
        />
      )}
    </Flex>
  );
};

export default PublicEvents;
